import { ref, toRaw, type Ref } from 'vue';

// Shared building blocks for the SWR cache stores (components, orders, samples, article
// table/cards). Both expose their backing record as `data` so stores keep full control
// over snapshot round-trips (toRaw), id pruning and tab-resume resets.

/**
 * SWR revalidations keep the existing object when the fresh response is identical, so
 * watchers and computeds keyed on object identity only fire on real changes. The JSON
 * comparison is key-order-sensitive — safe here because both sides come from the same
 * serializer, and snapshot seeds preserve key order through IndexedDB's structured clone.
 */
export function isUnchanged(existing: unknown, fresh: unknown): boolean {
  return JSON.stringify(toRaw(existing)) === JSON.stringify(fresh);
}

/**
 * Remove ids from every cached id-list, so deleted entities don't linger in the
 * keyed list/table caches.
 */
export function pruneIds<T extends { orderedIds: number[] }>(caches: Ref<Record<string, T>>, ids: number[]) {
  const idSet = new Set(ids);
  for (const cache of Object.values(caches.value)) {
    cache.orderedIds = cache.orderedIds.filter((id) => !idSet.has(id));
  }
}

/**
 * String-keyed cache of view/list states, LRU-capped to bound memory and snapshot size.
 * Delete-then-set keeps object insertion order = recency, so evicting the first keys is
 * LRU-ish. This only works for string keys — integer-like keys iterate in ascending
 * numeric order regardless of insertion (use entityCache for id-keyed data).
 */
export function keyedLruCache<T>(max: number) {
  const data = ref({} as Record<string, T>);

  function get(key: string): T | undefined {
    return data.value[key];
  }

  function set(key: string, value: T) {
    delete data.value[key];
    data.value[key] = value;
    const keys = Object.keys(data.value);
    for (let i = 0; i < keys.length - max; i++) {
      delete data.value[keys[i]];
    }
  }

  function clear() {
    data.value = {};
  }

  return { data, get, set, clear };
}

/**
 * Id-keyed DTO cache. Deliberately unbounded: it only ever holds what the user actually
 * viewed, and the snapshot layer's max age (lib/snapshot-cache.ts) bounds it over time.
 */
export function entityCache<T extends { id: number }>() {
  const data = ref({} as Record<number, T>);

  function get(id: number): T | undefined {
    return data.value[id];
  }

  function set(item: T) {
    data.value[item.id] = item;
  }

  function cache(items: T[]) {
    for (const item of items) set(item);
  }

  function remove(ids: number[]) {
    for (const id of ids) {
      delete data.value[id];
    }
  }

  // Replace the whole record (snapshot seeding).
  function seed(items: Record<number, T>) {
    data.value = items;
  }

  function clear() {
    data.value = {};
  }

  return { data, get, set, cache, remove, seed, clear };
}
